import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Link } from 'lucide-react';
import { DeliverableDependency } from '../types/deliverable';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface DeliverableSimple {
  id: string;
  title: string;
  status: string;
}

interface DeliverableDependencySelectorProps {
  deliverableId: string;
  allDeliverables: DeliverableSimple[];
}

const STATUS_COLORS: Record<string, string> = {
  VERIFIED: 'bg-green-100 text-green-700',
  DELIVERED: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  PENDING: 'bg-gray-100 text-gray-700',
  REJECTED: 'bg-red-100 text-red-700',
  DELAYED: 'bg-orange-100 text-orange-700',
};

export const DeliverableDependencySelector: React.FC<DeliverableDependencySelectorProps> = ({
  deliverableId,
  allDeliverables,
}) => {
  const [dependencies, setDependencies] = useState<DeliverableDependency[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getToken = () =>
    localStorage.getItem('authToken') || localStorage.getItem('token');

  useEffect(() => {
    if (!deliverableId) return;

    const fetchDeps = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/deliverables/${deliverableId}/dependencies`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!response.ok) throw new Error('Failed to fetch dependencies');
        const result = await response.json();
        setDependencies(result.data || result || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dependencies');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeps();
  }, [deliverableId]);

  const handleAdd = async () => {
    if (!selectedId) return;
    setIsAdding(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/deliverables/${deliverableId}/dependencies`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ dependsOnId: selectedId }),
        }
      );
      if (!response.ok) throw new Error('Failed to add dependency');
      const result = await response.json();
      const newDep: DeliverableDependency = result.data || result;
      setDependencies((prev) => [...prev, newDep]);
      setSelectedId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add dependency');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (dep: DeliverableDependency) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/deliverables/${deliverableId}/dependencies/${dep.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (!response.ok) throw new Error('Failed to remove dependency');
      setDependencies((prev) => prev.filter((d) => d.id !== dep.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove dependency');
    }
  };

  const existingDepIds = new Set(dependencies.map((d) => d.dependsOnId));
  const availableToAdd = allDeliverables.filter(
    (d) => d.id !== deliverableId && !existingDepIds.has(d.id)
  );

  const blockedDeps = dependencies.filter((d) => d.dependsOn.status !== 'VERIFIED');
  const isBlocked = blockedDeps.length > 0;

  if (isLoading) {
    return <div className="py-4 text-sm text-gray-500 text-center">Loading...</div>;
  }

  return (
    <div className="space-y-3">
      {isBlocked && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            Blocked: waiting for {blockedDeps.length} dependency(ies) to be VERIFIED
          </span>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-500">{error}</div>
      )}

      {dependencies.length === 0 ? (
        <p className="text-sm text-gray-500">No dependencies set.</p>
      ) : (
        <ul className="space-y-2">
          {dependencies.map((dep) => (
            <li
              key={dep.id}
              className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Link className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-800 truncate">{dep.dependsOn.title}</span>
                <span
                  className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                    STATUS_COLORS[dep.dependsOn.status] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {dep.dependsOn.status}
                </span>
              </div>
              <button
                onClick={() => handleRemove(dep)}
                className="text-gray-400 hover:text-red-500 flex-shrink-0"
                title="Remove dependency"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {availableToAdd.length > 0 && (
        <div className="flex items-center gap-2">
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Select a deliverable...</option>
            {availableToAdd.map((d) => (
              <option key={d.id} value={d.id}>
                {d.title} ({d.status})
              </option>
            ))}
          </select>
          <button
            onClick={handleAdd}
            disabled={!selectedId || isAdding}
            className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAdding ? 'Adding...' : 'Add'}
          </button>
        </div>
      )}
    </div>
  );
};

export default DeliverableDependencySelector;
